import { catchError, del, get, getBlob, post, put } from '@/modules/shared/http_handler'
import type { RequestError } from '@/modules/shared/domain/requestError'
import type { ApiMessage } from '@/modules/shared/apiText'
import type { LoginResponse, MeResponse, Profile } from '../domain/entity/auth'

export const login = (
  email: string,
  password: string,
  deviceFingerprint: string,
): Promise<[RequestError | undefined, LoginResponse | undefined]> => {
  return catchError(post<LoginResponse>('/web/auth/login', { email, password, clientFingerprint: deviceFingerprint }))
}

/**
 * A profile, or WHY there is none.
 *
 * `unreachable` means the request itself failed — the server was never heard
 * from. `noProfile` means it answered and had none to give. Callers that turn
 * a missing profile into a message have to tell those apart: the sign-in
 * screen used to call both of them wrong credentials.
 */
export type ProfileFetch =
  | { ok: true; profile: Profile }
  | { ok: false; reason: 'unreachable' | 'noProfile' }

export const fetchProfileResult = async (): Promise<ProfileFetch> => {
  const [error, response] = await catchError(get<MeResponse>('/web/auth/me'))
  if (error) {
    return { ok: false, reason: 'unreachable' }
  }

  if (!response?.success || !response.profile) {
    return { ok: false, reason: 'noProfile' }
  }

  const profile = response.profile
  return {
    ok: true,
    profile: {
    userId: profile.userId,
    email: profile.email,
    roles: profile.roles ?? [],
    permissions: [...(profile.permissions ?? [])],
    // Rebuilt rather than spread: the flag opens the platform section, so it
    // is granted only by a literal `true` on the wire. An API that stops
    // sending the field leaves it undefined, and a spread would carry that
    // straight into a truthiness check somewhere downstream.
    isPlatformAdmin: profile.isPlatformAdmin === true,
    firstName: profile.firstName ?? null,
    lastName: profile.lastName ?? null,
    idNumber: profile.idNumber ?? null,
    phoneNumber: profile.phoneNumber ?? null,
      address: profile.address ?? null,
      mustChangePassword: profile.mustChangePassword === true,
    },
  }
}

export const fetchProfile = async (): Promise<Profile | null> => {
  const result = await fetchProfileResult()
  return result.ok ? result.profile : null
}

export interface OwnProfileDraft {
  firstName: string
  lastName: string
  phoneNumber: string
  address: string | null
}

export interface OwnProfileResponse {
  success: boolean
  message: ApiMessage | null
}

export const updateOwnProfile = (
  draft: OwnProfileDraft,
): Promise<[RequestError | undefined, OwnProfileResponse | undefined]> => {
  return catchError(put<OwnProfileResponse>('/web/profile', { ...draft }))
}

export const uploadOwnPhoto = (
  file: File,
): Promise<[RequestError | undefined, OwnProfileResponse | undefined]> => {
  const body = new FormData()
  body.append('file', file)
  return catchError(post<OwnProfileResponse>('/web/profile/photo', body))
}

export const removeOwnPhoto = (): Promise<
  [RequestError | undefined, OwnProfileResponse | undefined]
> => {
  return catchError(del<OwnProfileResponse>('/web/profile/photo'))
}

/**
 * A plain <img src> carries no Authorization header, so the endpoint would
 * refuse it. The bytes come through the authenticated client and become an
 * object url the browser can render.
 */
export const fetchOwnPhoto = async (): Promise<string | null> => {
  // catchError already unwraps the axios response, so this IS the blob.
  const [error, blob] = await catchError(getBlob('/web/profile/photo'))
  return error || !blob ? null : URL.createObjectURL(blob)
}

export interface PasswordResetResponse {
  success: boolean
  message: ApiMessage | null
}

export const requestPasswordReset = (
  email: string,
  localeCode: string,
): Promise<[RequestError | undefined, PasswordResetResponse | undefined]> => {
  return catchError(post<PasswordResetResponse>('/web/auth/forgot-password', { email, localeCode }))
}

export const completePasswordReset = (
  token: string,
  password: string,
): Promise<[RequestError | undefined, PasswordResetResponse | undefined]> => {
  return catchError(post<PasswordResetResponse>('/web/auth/reset-password', { token, password }))
}
